import React from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

export const ProdottiOrologeria = () => {
  const { state: product } = useLocation();
  const [isImageZoomed, setIsImageZoomed] = React.useState(false);

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!product) {
    return (
      <div className="flex flex-col items-center bg-gradient-to-b from-gray-50 to-white w-full min-h-screen">
        <Navbar />
        <p className="text-center text-red-500 font-elegant mt-12" role="alert">
          Nessun prodotto selezionato.
        </p>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gradient-to-b from-gray-50 to-white w-full font-elegant">
      <Navbar />

      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-12">
        {/* Breadcrumb */}
        <nav className="mb-4 sm:mb-6 md:mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
            <li><a href="/" className="hover:text-green-800 transition-colors">Home</a></li>
            <li><span className="text-gray-400">/</span></li>
            <li><a href="/orologeria" className="hover:text-green-800 transition-colors">Orologeria</a></li>
            <li><span className="text-gray-400">/</span></li>
            <li className="text-gray-800 font-medium truncate max-w-[100px] sm:max-w-[150px]">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
          {/* Image Section with Zoom */}
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden lg:sticky lg:top-24">
              <button
                className="aspect-square relative bg-gray-100 flex items-center justify-center p-4 sm:p-6 md:p-8 w-full cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-green-800"
                onClick={() => setIsImageZoomed(true)}
                aria-label="Clicca per ingrandire l'immagine"
              >
                <img
                  src={product.image}
                  alt={`${product.name} - ${product.material}`}
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                />
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                  </svg>
                  Zoom
                </div>
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center">Clicca sull'immagine per ingrandirla</p>
          </div>

          {/* Fullscreen Image Modal */}
          {isImageZoomed && (
            <div
              className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
              onClick={() => setIsImageZoomed(false)}
              role="dialog"
              aria-label="Immagine ingrandita"
            >
              <button
                className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 focus:outline-none focus:ring-4 focus:ring-white"
                onClick={() => setIsImageZoomed(false)}
                aria-label="Chiudi zoom"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={product.image}
                alt={`${product.name} - ${product.material} (ingrandita)`}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Details Section */}
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3">
                {product.name}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600">
                {product.marca}
              </p>
            </div>

            {/* Price */}
            <div className="bg-green-50 border-2 border-green-800 rounded-lg sm:rounded-xl p-4 sm:p-6">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Prezzo</p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-800">
                {product.price}
              </p>
            </div>

            {/* Product Info */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6 space-y-3 sm:space-y-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Dettagli Prodotto</h2>

              <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-800 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1">Marca</p>
                  <p className="text-sm sm:text-base font-semibold text-gray-800">{product.marca}</p>
                </div>
              </div>

              <div className="flex items-start gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-800 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1">Materiale</p>
                  <p className="text-sm sm:text-base font-semibold text-gray-800">{product.material}</p>
                </div>
              </div>

              {product.description && (
                <div className="pt-1 sm:pt-2">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Descrizione</p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{product.description}</p>
                </div>
              )}
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-green-800 to-green-900 rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 md:p-8 text-white">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3">Interessato a questo prodotto?</h3>
              <p className="text-xs sm:text-sm md:text-base mb-4 sm:mb-6 opacity-90">
                Contattaci per maggiori informazioni, disponibilità e per fissare un appuntamento nel nostro negozio.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <a
                  href="tel:+39039388876"
                  className="flex items-center justify-center gap-2 bg-white text-green-800 hover:bg-gray-100 font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:ring-4 focus:ring-white/50"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Chiama Ora
                </a>
                <a
                  href="/contattaci"
                  className="flex items-center justify-center gap-2 bg-green-700 hover:bg-green-600 text-white font-semibold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:ring-4 focus:ring-white/50"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Invia Email
                </a>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
              <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-3">Condividi questo prodotto</h3>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {/* WhatsApp Share */}
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Guarda questo prodotto: ${product.name} - ${window.location.href}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-400"
                  aria-label="Condividi su WhatsApp"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>

                {/* Facebook Share */}
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#1877F2] hover:bg-[#1665D8] text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                  aria-label="Condividi su Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>

                {/* Copy Link */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copiato negli appunti!');
                  }}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                  aria-label="Copia link"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copia Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
